// frontend/src/components/TransacaoMilhasModal/TransacaoMilhasModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, DatePicker, Button, message, InputNumber, Row, Col, Input, Radio } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { getPortadores, getProgramasDoPortador, createCompraBonificada, createCompraPontos, createPontosCartao, createBonusRecebidos, updateTransacaoMilhas, createEntradaAvulsa } from '../../services/milhasService';
import { currencyInputProps as sharedCurrencyInputProps, decimalInputProps } from '../../utils/numberInputMask';
import { getErrorMessage } from '../../utils/errorHandler';
import { preserveMarcadorFaturaId, stripMarcadorFaturaId } from '../../utils/estimativaPontosCartao';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;

const pointsFormatter = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return new Intl.NumberFormat('pt-BR').format(value);
};

const pointsParser = (value) => {
    if (!value) return null;
    return String(value).replace(/\./g, '');
};

const currencyInputProps = sharedCurrencyInputProps();

const costInputProps = decimalInputProps(4, { prefix: 'R$ ', min: 0 });

const pontosPorRealInputProps = decimalInputProps(1, { min: 0 });

const pointsInputProps = {
    style: { width: '100%' },
    min: 0,
    formatter: pointsFormatter,
    parser: pointsParser,
    precision: 0,
    inputMode: 'numeric',
};

const TransacaoMilhasModal = ({ visible, onClose, onSuccess, onRefresh, editingTransaction, isMobile }) => {
    const { user: authUser } = useAuth();
    const wsCtx = useWorkspace();
    const compartilhamentoAtivo = !!(
        wsCtx?.emWorkspaceCompartilhado || wsCtx?.concedido
        || (wsCtx?.recebidos && wsCtx.recebidos.length > 0)
    );
    const lancadoPorMilhas = compartilhamentoAtivo && editingTransaction?.criado_por_nome
        ? (editingTransaction?.criado_por_id === authUser?.id ? 'você' : editingTransaction.criado_por_nome)
        : null;
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    
    const [subTipo, setSubTipo] = useState(null);
    const [destino, setDestino] = useState('USO_PROPRIO');
    const [portadores, setPortadores] = useState([]);
    const [programas, setProgramas] = useState([]);
    const [loadingPortadores, setLoadingPortadores] = useState(false);
    const [loadingProgramas, setLoadingProgramas] = useState(false);
    const [selectedPortador, setSelectedPortador] = useState(null);
    const [selectedProgramaData, setSelectedProgramaData] = useState(null);
    const [tipoCusto, setTipoCusto] = useState('valorTotal');
    
    const [valorProduto, setValorProduto] = useState(0);
    const [pontosPorReal, setPontosPorReal] = useState(1);
    const [valorVenda, setValorVenda] = useState(0);
    const [valorTotal, setValorTotal] = useState(0);
    const [custoMilheiro, setCustoMilheiro] = useState(0);
    const [quantidadeMilhasPontos, setQuantidadeMilhasPontos] = useState(0);

    const resetModalStates = useCallback(() => {
        setSubTipo(null);
        setDestino('USO_PROPRIO');
        setProgramas([]);
        setSelectedPortador(null);
        setSelectedProgramaData(null);
        setTipoCusto('valorTotal');
        setValorProduto(0);
        setPontosPorReal(1);
        setValorVenda(0);
        setValorTotal(0);
        setCustoMilheiro(0);
        setQuantidadeMilhasPontos(0);
        form.resetFields();
    }, [form]);

    const fetchProgramas = useCallback(async (portadorId, programaIdParaSelecionar = null) => {
        if (!portadorId) return;
        setLoadingProgramas(true);
        try {
            const res = await getProgramasDoPortador(portadorId);
            setProgramas(res.data);
            if (programaIdParaSelecionar && res.data) {
                const programaDoPortador = res.data.find(p => p.id === programaIdParaSelecionar);
                if (programaDoPortador) {
                    form.setFieldsValue({ programa_portador: programaDoPortador.id });
                    setSelectedProgramaData(programaDoPortador);
                }
            }
        } catch (error) {
            messageApi.error('Falha ao carregar os programas deste portador.');
        } finally {
            setLoadingProgramas(false);
        }
    }, [form, messageApi]);
    
    useEffect(() => {
        const fetchDadosIniciais = async () => {
            setLoadingPortadores(true);
            try {
                const res = await getPortadores();
                const lista = res.data || [];
                setPortadores(lista);

                if (editingTransaction) {
                    const portadorId = editingTransaction.portador_id;
                    const programaId = editingTransaction.programa_portador;
                    setSelectedPortador(portadorId);
                    form.setFieldsValue({ portador: portadorId });
                    await fetchProgramas(portadorId, programaId);
                } else if (lista.length === 1) {
                    // Com um único portador não há o que escolher: já deixa preenchido.
                    const unico = lista[0];
                    form.setFieldsValue({ portador: unico.id });
                    setSelectedPortador(unico.id);
                    fetchProgramas(unico.id);
                }
            } catch (error) {
                messageApi.error('Falha ao carregar a lista de portadores.');
            } finally {
                setLoadingPortadores(false);
            }
        };

        if (visible) {
            if (!editingTransaction) {
                resetModalStates();
            }
            fetchDadosIniciais();
        }
    }, [visible, editingTransaction, form, fetchProgramas, resetModalStates, messageApi]);


    useEffect(() => {
        if (visible && editingTransaction) {
            const detalhes = editingTransaction.detalhes_compra_bonificada;
            let formValues = {
                sub_tipo: editingTransaction.sub_tipo,
                quantidade_milhas: editingTransaction.quantidade_milhas,
                data_transacao: editingTransaction.data_transacao ? dayjs(editingTransaction.data_transacao) : null,
                observacao: stripMarcadorFaturaId(editingTransaction.observacao),
            };

            if (detalhes) {
                formValues = { ...formValues, produto: detalhes.produto, loja: detalhes.loja, pontos_por_real: detalhes.pontos_por_real, valor_produto: detalhes.valor_produto, destino: detalhes.destino, valor_venda: detalhes.valor_venda, data_compra: dayjs(detalhes.data_compra), data_recebimento_pontos: dayjs(detalhes.data_recebimento_pontos) };
            }

            if (['COMPRA_PONTOS', 'PONTOS_CARTAO', 'ENTRADA_AVULSA', 'PONTOS_CLUBE'].includes(editingTransaction.sub_tipo)) {
                formValues.valor_total = editingTransaction.custo_total_transacao;
                formValues.tipo_custo = 'valorTotal';
                if (editingTransaction.custo_total_transacao > 0 && editingTransaction.quantidade_milhas > 0) {
                    formValues.custo_milheiro = (parseFloat(editingTransaction.custo_total_transacao) / parseFloat(editingTransaction.quantidade_milhas)) * 1000;
                }
            }
            
            setSubTipo(editingTransaction.sub_tipo);
            setDestino(detalhes?.destino || 'USO_PROPRIO');
            setQuantidadeMilhasPontos(editingTransaction.quantidade_milhas);
            setValorTotal(editingTransaction.custo_total_transacao);
            if (editingTransaction.custo_total_transacao > 0 && editingTransaction.quantidade_milhas > 0) {
                setCustoMilheiro((parseFloat(editingTransaction.custo_total_transacao) / parseFloat(editingTransaction.quantidade_milhas)) * 1000);
            }

            form.setFieldsValue(formValues);
        }
    }, [visible, editingTransaction, form]);

    useEffect(() => {
        if (subTipo === 'COMPRA_BONIFICADA' && valorProduto > 0 && pontosPorReal > 0) {
            form.setFieldsValue({ quantidade_milhas: Math.floor(valorProduto * pontosPorReal) });
        }
    }, [valorProduto, pontosPorReal, subTipo, form]);

    useEffect(() => {
        if (['COMPRA_PONTOS', 'PONTOS_CARTAO', 'ENTRADA_AVULSA', 'PONTOS_CLUBE'].includes(subTipo)) {
            if (tipoCusto === 'valorTotal' && quantidadeMilhasPontos > 0 && valorTotal >= 0) {
                form.setFieldsValue({ custo_milheiro: (valorTotal / quantidadeMilhasPontos) * 1000 });
            } else if (tipoCusto === 'custoMilheiro' && quantidadeMilhasPontos > 0 && custoMilheiro >= 0) {
                form.setFieldsValue({ valor_total: (custoMilheiro * quantidadeMilhasPontos) / 1000 });
            }
        }
    }, [subTipo, tipoCusto, valorTotal, custoMilheiro, quantidadeMilhasPontos, form]);
    
    const handleValuesChange = (changedValues) => {
        if (changedValues.sub_tipo !== undefined) setSubTipo(changedValues.sub_tipo);
        if (changedValues.destino !== undefined) setDestino(changedValues.destino);
        if (changedValues.valor_produto !== undefined) setValorProduto(changedValues.valor_produto);
        if (changedValues.pontos_por_real !== undefined) setPontosPorReal(changedValues.pontos_por_real);
        if (changedValues.valor_venda !== undefined) setValorVenda(changedValues.valor_venda);
        if (changedValues.tipo_custo !== undefined) setTipoCusto(changedValues.tipo_custo);
        if (changedValues.valor_total !== undefined) setValorTotal(changedValues.valor_total);
        if (changedValues.custo_milheiro !== undefined) setCustoMilheiro(changedValues.custo_milheiro);
        if (changedValues.quantidade_milhas !== undefined) setQuantidadeMilhasPontos(changedValues.quantidade_milhas);
    };

    const handlePortadorChange = async (portadorId) => {
        setSelectedPortador(portadorId);
        form.setFieldsValue({ programa_portador: null });
        setProgramas([]);
        setSelectedProgramaData(null);
        await fetchProgramas(portadorId);
    };

    const handleProgramaChange = (programaId) => setSelectedProgramaData(programas.find(p => p.id === programaId));

    const getValorMilheiro = () => {
        const custo = destino === 'VENDA' ? (valorProduto || 0) - (valorVenda || 0) : (destino === 'USO_PROPRIO' ? 0 : (valorProduto || 0));
        const pontos = form.getFieldValue('quantidade_milhas');
        if (!pontos || pontos <= 0) return 0;
        return (custo / pontos) * 1000;
    };

    const handleFormSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            setLoading(true);
    
            const dateFields = ['data_transacao', 'data_compra', 'data_recebimento_pontos'];
            dateFields.forEach(field => {
                if (values[field] && dayjs.isDayjs(values[field])) {
                    values[field] = values[field].format('YYYY-MM-DD');
                }
            });

            let apiCall;
            let successMessage = "";
            let isUpdate = !!editingTransaction;
            
            if (isUpdate) {
                let payload = { ...values };
                delete payload.portador;
                delete payload.programa_portador;
                delete payload.sub_tipo;
                if ('observacao' in payload) {
                    payload.observacao = preserveMarcadorFaturaId(
                        payload.observacao,
                        editingTransaction.observacao,
                    );
                }
    
                apiCall = updateTransacaoMilhas(editingTransaction.id, payload);
                const subTipoLabel = subTiposEntrada.find(s => s.value === editingTransaction.sub_tipo)?.label || "Movimentação";
                successMessage = subTipoLabel;
            } else {
                const subTipoValue = values.sub_tipo;
    
                if (subTipoValue === 'COMPRA_BONIFICADA') {
                    const payload = {
                        programa_portador: values.programa_portador,
                        quantidade_milhas: values.quantidade_milhas,
                        observacao: values.observacao,
                        produto: values.produto,
                        loja: values.loja,
                        pontos_por_real: values.pontos_por_real,
                        valor_produto: values.valor_produto,
                        destino: values.destino,
                        valor_venda: values.valor_venda,
                        data_compra: values.data_compra,
                        data_recebimento_pontos: values.data_recebimento_pontos,
                    };
                    apiCall = createCompraBonificada(payload);
                    successMessage = "Compra Bonificada";
                } else if (['COMPRA_PONTOS', 'ENTRADA_AVULSA', 'PONTOS_CARTAO'].includes(subTipoValue)) {
                    const payload = {
                        programa_portador: values.programa_portador,
                        quantidade_milhas: values.quantidade_milhas,
                        data_transacao: values.data_transacao,
                        observacao: values.observacao,
                    };
                    if (values.tipo_custo === 'valorTotal') {
                        payload.custo_total_transacao = values.valor_total;
                    } else {
                        payload.custo_milheiro = values.custo_milheiro;
                    }
                    if (subTipoValue === 'COMPRA_PONTOS') {
                        apiCall = createCompraPontos(payload);
                        successMessage = "Compra de Pontos";
                    } else if (subTipoValue === 'ENTRADA_AVULSA') {
                        apiCall = createEntradaAvulsa(payload);
                        successMessage = "Entrada Avulsa";
                    } else { // PONTOS_CARTAO
                        apiCall = createPontosCartao(payload);
                        successMessage = "Pontos de Cartão";
                    }
                } else if (subTipoValue === 'BONUS_RECEBIDOS') {
                    const payload = {
                        programa_portador: values.programa_portador,
                        quantidade_milhas: values.quantidade_milhas,
                        data_transacao: values.data_transacao,
                        observacao: values.observacao,
                    };
                    apiCall = createBonusRecebidos(payload);
                    successMessage = "Bônus Recebidos";
                }
            }
            
            await apiCall;
            const finalMsg = `${successMessage} ${isUpdate ? 'atualizada' : 'registrada'} com sucesso!`;
            
            if (onRefresh) onRefresh();
    
            onSuccess(closeOnSuccess, finalMsg);

            if (!closeOnSuccess) {
                const fieldsToKeep = {
                    portador: values.portador,
                    programa_portador: values.programa_portador,
                    sub_tipo: subTipo,
                };
                resetModalStates();
                form.setFieldsValue(fieldsToKeep);
                setSelectedPortador(fieldsToKeep.portador);
                fetchProgramas(fieldsToKeep.portador, fieldsToKeep.programa_portador);
                setSubTipo(fieldsToKeep.sub_tipo);
            }
    
        } catch (error) {
            if (error?.errorFields) return;
            messageApi.error(getErrorMessage(error, 'Erro ao salvar. Verifique os campos e tente novamente.'));
        } finally {
            setLoading(false);
        }
    };
    
    const handleSubmit = () => handleFormSubmit(true);
    const handleSubmitAndAddNew = () => handleFormSubmit(false);

    const subTiposEntrada = [
        { value: 'COMPRA_BONIFICADA', label: 'Compra Bonificada' },
        { value: 'COMPRA_PONTOS', label: 'Compra de Pontos' },
        { value: 'ENTRADA_AVULSA', label: 'Entrada Avulsa' },
        { value: 'PONTOS_CARTAO', label: 'Pontos de Cartão' },
        { value: 'BONUS_RECEBIDOS', label: 'Bônus Recebidos' },
        { value: 'PONTOS_CLUBE', label: 'Pontos de Clube', disabled: !editingTransaction },
    ];
    
    // Renderização dos campos com Grid Responsivo
    const renderCompraBonificadaFields = () => (
        <>
            <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item name="data_compra" label="Data da Compra" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="data_recebimento_pontos" label="Data Prev. Receb. Pontos" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item name="produto" label="Produto" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="loja" label="Loja" rules={[{ required: true }]}><Input /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item name="valor_produto" label="Valor Total do Produto" rules={[{ required: true }]}><InputNumber {...currencyInputProps} /></Form.Item></Col>
                <Col xs={24} sm={12}>
                    <Form.Item name="pontos_por_real" label="Pontos por Real" rules={[{ required: true }]} initialValue={1}>
                        <InputNumber {...pontosPorRealInputProps} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item name="destino" label="Destino" initialValue="USO_PROPRIO"><Radio.Group><Radio value="USO_PROPRIO">Uso próprio</Radio><Radio value="VENDA">Para venda</Radio></Radio.Group></Form.Item></Col>
                {destino === 'VENDA' && (<Col xs={24} sm={12}><Form.Item name="valor_venda" label="Valor da Venda" rules={[{ required: true }]}><InputNumber {...currencyInputProps} /></Form.Item></Col>)}
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item name="quantidade_milhas" label="Quantidade de Pontos/Milhas" rules={[{ required: true }]}><InputNumber {...pointsInputProps} /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item label="Custo do Milheiro (CPM)"><Input prefix="R$ " value={getValorMilheiro().toFixed(2).replace('.',',')} disabled /></Form.Item></Col>
            </Row>
        </>
    );

    const renderGenericEntryFields = () => ( 
        <> 
            <Row gutter={16}>
                <Col xs={24} sm={12}><Form.Item name="quantidade_milhas" label="Quantidade de Pontos/Milhas" rules={[{ required: true }]}><InputNumber {...pointsInputProps} /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="data_transacao" label="Data da Transação" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
            </Row> 
            <Form.Item name="tipo_custo" label="Tipo de Custo" initialValue="valorTotal"><Radio.Group><Radio value="valorTotal">Valor Total</Radio><Radio value="custoMilheiro">Custo do Milheiro (CM)</Radio></Radio.Group></Form.Item> 
            {tipoCusto === 'valorTotal' && (<Form.Item name="valor_total" label="Valor Total da Compra"><InputNumber {...currencyInputProps} /></Form.Item>)} 
            {tipoCusto === 'custoMilheiro' && (<Form.Item name="custo_milheiro" label="Custo do Milheiro (CM)"><InputNumber {...costInputProps} /></Form.Item>)} 
        </> 
    );

    const renderBonusRecebidosFields = () => ( 
        <Row gutter={16}>
            <Col xs={24} sm={12}><Form.Item name="quantidade_milhas" label="Quantidade de Pontos/Milhas" rules={[{ required: true }]}><InputNumber {...pointsInputProps} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="data_transacao" label="Data da Transação" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
        </Row> 
    );

    const renderFieldsBySubtype = () => { switch (subTipo) { case 'COMPRA_BONIFICADA': return renderCompraBonificadaFields(); case 'COMPRA_PONTOS': case 'ENTRADA_AVULSA': case 'PONTOS_CARTAO': case 'PONTOS_CLUBE': return renderGenericEntryFields(); case 'BONUS_RECEBIDOS': return renderBonusRecebidosFields(); default: return <p>Selecione um tipo de operação.</p>; } };

    // Footer desktop
    const renderFooter = () => [
        <Button key="back" onClick={onClose}>Cancelar</Button>,
        !editingTransaction && <Button key="add" type="default" loading={loading} onClick={handleSubmitAndAddNew}>Salvar e Adicionar Outro</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>Salvar</Button>,
    ];

    const sheetTitle = editingTransaction ? 'Editar Movimentação' : 'Entrada de Milhas';

    const formContent = (
        <Form form={form} layout="vertical" onValuesChange={handleValuesChange} size={isMobile ? 'large' : 'middle'} scrollToFirstError>
                <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Portador" name="portador" rules={[{ required: true, message: 'Obrigatório' }]} style={{ marginBottom: isMobile ? 12 : 0 }}>
                            <Select style={{ width: '100%' }} placeholder="Selecione o portador" onChange={handlePortadorChange} loading={loadingPortadores} showSearch filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())} disabled={!!editingTransaction}>
                                {portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Programa" name="programa_portador" rules={[{ required: true, message: 'Obrigatório' }]} style={{ marginBottom: 0 }}>
                            <Select style={{ width: '100%' }} placeholder={!selectedPortador ? "Selecione um portador" : "Selecione o programa"} disabled={!selectedPortador || loadingProgramas || !!editingTransaction} loading={loadingProgramas} onChange={handleProgramaChange} showSearch filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
                                {programas.map(prog => <Option key={prog.id} value={prog.id}>{prog.programa_nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                {selectedProgramaData && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={12} style={{ marginBottom: isMobile ? 8 : 0 }}><div style={{ padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px' }}><div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5A6A6A)' }}>Saldo Atual</div><div style={{ fontSize: '16px', fontWeight: 'bold' }}>{parseFloat(selectedProgramaData.saldo_atual).toLocaleString('pt-BR')} pts</div></div></Col>
                        <Col xs={24} sm={12}><div style={{ padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px' }}><div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5A6A6A)' }}>Custo Médio do Milheiro (CM)</div><div style={{ fontSize: '16px', fontWeight: 'bold' }}>R$ {parseFloat(selectedProgramaData.custo_medio_milheiro).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div></Col>
                    </Row>
                )}
                <Form.Item name="sub_tipo" label="Operação de Entrada" rules={[{ required: true, message: 'Selecione a operação.' }]}>
                    <Select placeholder="Selecione uma operação" onChange={(value) => setSubTipo(value)} disabled={!!editingTransaction}>
                        {subTiposEntrada
                            .filter(subtipo => !subtipo.disabled || (editingTransaction && subtipo.value === editingTransaction.sub_tipo))
                            .map(subtipo => ( <Option key={subtipo.value} value={subtipo.value}>{subtipo.label}</Option> ))
                        }
                    </Select>
                </Form.Item>
                {renderFieldsBySubtype()}
                <Form.Item name="observacao" label="Observação"><Input.TextArea rows={2} /></Form.Item>
                {editingTransaction && lancadoPorMilhas && (
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--color-text-secondary, #8a8a8a)' }}>
                        <UserOutlined style={{ marginRight: 6 }} />
                        Lançado por {lancadoPorMilhas}
                    </div>
                )}
            </Form>
    );

    if (isMobile) {
        return (
            <>
                {contextHolder}
                <MobileLaunchSheet
                    open={visible}
                    title={sheetTitle}
                    onCancel={onClose}
                    onSave={handleSubmit}
                    saveLoading={loading}
                    secondaryAction={
                        !editingTransaction ? (
                            <Button type="default" loading={loading} onClick={handleSubmitAndAddNew} block size="large">
                                Salvar e Novo
                            </Button>
                        ) : null
                    }
                >
                    {formContent}
                </MobileLaunchSheet>
            </>
        );
    }

    return (
        <>
            {contextHolder}
            <Modal 
                title={editingTransaction ? "Editar Movimentação de Milhas" : "Lançar Nova Movimentação de Milhas"} 
                open={visible} 
                onCancel={onClose} 
                width={800}
                destroyOnClose
                footer={renderFooter()}
            >
                {formContent}
            </Modal>
        </>
    );
};

export default TransacaoMilhasModal;